if (!customElements.get('mini-cart')) {
  customElements.define('mini-cart', class MiniCart extends HTMLElement {
    connectedCallback() {
      // Overlay lookup is deferred to open() — the overlay div sits after this element
      // in the HTML, so at connectedCallback time it may not yet be in the DOM.
      this.itemsEl  = document.getElementById('mini-cart-items');
      this.totalEl  = document.getElementById('mini-cart-total');
      this.footerEl = document.getElementById('mini-cart-footer');
      this.bindEvents();
    }

    // product-form.js calls getSectionsToRender() to know which Shopify sections to
    // re-render server-side. We handle rendering ourselves, so return an empty array.
    getSectionsToRender() {
      return [];
    }

    // product-form.js passes the active element so we can restore focus on close.
    setActiveElement(element) {
      this.activeElement = element;
    }

    // product-form.js calls this after a successful add-to-cart POST.
    // The `response` arg is the /cart/add.js item payload — we ignore it and fetch
    // the full cart ourselves so we always have up-to-date totals and all items.
    renderContents() {
      this.fetchCart()
        .then(cart => {
          this.renderCart(cart);
          this.updateCartCount(cart.item_count);
          this.open();
        })
        .catch(err => console.error('[mini-cart] fetch error:', err));
    }

    fetchCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json());
    }

    renderCart(cart) {
      if (!cart.items || cart.items.length === 0) {
        this.itemsEl.innerHTML = '<p class="mini-cart__empty">Your basket is empty.</p>';
        this.footerEl.hidden = true;
        return;
      }

      this.itemsEl.innerHTML = cart.items.map(item => this.renderItem(item)).join('');
      this.totalEl.textContent = this.formatMoney(cart.total_price, cart.currency);
      this.footerEl.hidden = false;

      this.querySelectorAll('[data-mini-cart-remove]').forEach(btn => {
        btn.addEventListener('click', () => {
          btn.disabled = true;
          this.removeItem(btn.dataset.miniCartRemove);
        });
      });

      this.querySelectorAll('[data-mini-cart-qty]').forEach(btn => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.miniCartQty;
          const delta = parseInt(btn.dataset.delta, 10);
          const countEl = btn.closest('.mini-cart__item-qty').querySelector('.mini-cart__item-qty-count');
          const newQty = Math.max(0, parseInt(countEl.textContent, 10) + delta);
          btn.closest('.mini-cart__item-qty').querySelectorAll('[data-mini-cart-qty]').forEach(b => b.disabled = true);
          this.updateItemQuantity(key, newQty);
        });
      });
    }

    renderItem(item) {
      const overrideUrl = window.miniCartImageOverrides && window.miniCartImageOverrides[item.product_id];
      const imageUrl = overrideUrl || (item.featured_image && item.featured_image.url);

      let imageHtml = '';
      if (imageUrl) {
        const sep = imageUrl.includes('?') ? '&' : '?';
        imageHtml = `<img src="${imageUrl}${sep}width=200" alt="${this.attr(item.product_title)}" width="80" loading="lazy">`;
      }

      let variantHtml = '';
      if (item.options_with_values) {
        variantHtml = item.options_with_values
          .filter(o => o.value && o.value !== 'Default Title' && o.name !== 'Wiring type')
          .map(o => `<div class="mini-cart__item-variant">${this.html(o.name)}: ${this.html(o.value)}</div>`)
          .join('');
      }

      return `
        <div class="mini-cart__item">
          <div class="mini-cart__item-image">
            <a href="${this.attr(item.url)}">${imageHtml}</a>
          </div>
          <div class="mini-cart__item-details">
            <a href="${this.attr(item.url)}" class="mini-cart__item-name">${this.html(item.product_title)}</a>
            <div class="mini-cart__item-price">${this.formatMoney(item.final_price, item.currency)}</div>
            ${variantHtml}
            <div class="mini-cart__item-qty">
              <span class="mini-cart__item-qty-label">Qty:</span>
              <button class="mini-cart__item-qty-btn" type="button" data-mini-cart-qty="${this.attr(item.key)}" data-delta="-1" aria-label="Decrease quantity">−</button>
              <span class="mini-cart__item-qty-count">${item.quantity}</span>
              <button class="mini-cart__item-qty-btn" type="button" data-mini-cart-qty="${this.attr(item.key)}" data-delta="1" aria-label="Increase quantity">+</button>
            </div>
            <button
              class="mini-cart__item-remove"
              data-mini-cart-remove="${this.attr(item.key)}"
              type="button"
            >Remove</button>
          </div>
        </div>
      `.trim();
    }

    removeItem(key) {
      this.updateItemQuantity(key, 0);
    }

    updateItemQuantity(key, quantity) {
      fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      })
        .then(r => r.json())
        .then(cart => {
          this.updateCartCount(cart.item_count);
          if (cart.item_count === 0) {
            this.close();
          } else {
            this.renderCart(cart);
          }
        })
        .catch(err => console.error('[mini-cart] cart update error:', err));
    }

    updateCartCount(count) {
      document.querySelectorAll('.cart-count-text').forEach(el => {
        el.textContent = `${count}`;
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    open() {
      // Lazily resolve the overlay the first time we open — DOM is definitely ready by now.
      if (!this.overlay) {
        this.overlay = document.getElementById('mini-cart-overlay');
        if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      }
      this.classList.add('mini-cart--active');
      this.removeAttribute('aria-hidden');
      if (this.overlay) this.overlay.classList.add('mini-cart__opacity--active');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('mini-cart--active');
      this.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('mini-cart__opacity--active');
      document.body.style.overflow = '';
      if (this.activeElement) this.activeElement.focus();
    }

    bindEvents() {
      const closeIcon = this.querySelector('.mini-cart__close-icon');
      if (closeIcon) closeIcon.addEventListener('click', () => this.close());

      // Header cart icon — intercept both desktop and mobile links.
      // script is defer so the header is in the DOM by the time this runs.
      document.querySelectorAll('.header__icon--cart').forEach(link => {
        link.addEventListener('click', e => {
          e.preventDefault();
          this.fetchCart()
            .then(cart => {
              this.renderCart(cart);
              this.updateCartCount(cart.item_count);
              this.open();
            })
            .catch(err => console.error('[mini-cart] header cart error:', err));
        });
      });

      // Overlay click bound lazily in open() — see comment in connectedCallback.
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.classList.contains('mini-cart--active')) this.close();
      });
    }

    formatMoney(cents, currency) {
      const active = window.Shopify?.currency?.active || currency || 'GBP';
      const symbols = { GBP: '£', USD: '$', EUR: '€' };
      const symbol = symbols[active] || active + ' ';
      return symbol + (cents / 100).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    html(str) {
      if (!str) return '';
      const d = document.createElement('div');
      d.appendChild(document.createTextNode(String(str)));
      return d.innerHTML;
    }

    attr(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  });
}
